/**
 * OrderSystemEvents — per-order technical timeline.
 *
 * Renders below the main order detail content. Pulls the order-scoped
 * subset of system_logs so admins (and the order owner) can see exactly
 * what the backend did with this order — upload completions, checksum
 * outcomes, email delivery, repro events, etc.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Button } from './ui/button';
import { AlertOctagon, AlertTriangle, Info, RefreshCw, Activity } from 'lucide-react';
import { AdminCollapsibleSection } from './AdminCollapsibleSection';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LEVEL_ICON = {
    ERROR: { Icon: AlertOctagon, color: 'text-red-600',    dot: 'bg-red-500' },
    WARNING: { Icon: AlertTriangle, color: 'text-amber-600', dot: 'bg-amber-500' },
    INFO:    { Icon: Info,        color: 'text-slate-500', dot: 'bg-slate-400' },
};

function formatTimeOnly(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function formatDateGroup(iso) {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString([], { weekday: 'short', day: '2-digit', month: 'short' });
}

export default function OrderSystemEvents({ orderId }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedId, setExpandedId] = useState(null);

    const fetchEvents = useCallback(async () => {
        if (!orderId) return;
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}/api/orders/${orderId}/system-events`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            });
            if (!res.ok) {
                setItems([]);
                return;
            }
            const data = await res.json();
            setItems(data.items || []);
        } finally {
            setLoading(false);
        }
    }, [orderId]);

    useEffect(() => { fetchEvents(); }, [fetchEvents]);

    // Group by date
    const groups = items.reduce((acc, item) => {
        const dateKey = (item.created_at || '').slice(0, 10);
        if (!acc[dateKey]) acc[dateKey] = [];
        acc[dateKey].push(item);
        return acc;
    }, {});
    const groupKeys = Object.keys(groups).sort();

    return (
        <AdminCollapsibleSection
            id="system-events"
            title="System Events"
            icon={Activity}
            iconColor="text-indigo-500"
            statusLabel={!loading ? `${items.length} event${items.length === 1 ? '' : 's'}` : null}
            summary="Backend timeline for this order — uploads, checksums, emails, repro events"
            defaultOpen={false}
        >
            <div className="flex justify-end mb-2">
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={fetchEvents}
                    disabled={loading}
                    className="h-7 px-2"
                    data-testid="refresh-events-btn"
                >
                    <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
                    <span className="ml-1 text-xs">Refresh</span>
                </Button>
            </div>
            {items.length === 0 && !loading && (
                <div className="text-xs text-slate-400 py-4 text-center">
                    No system events recorded for this order yet.
                </div>
            )}
            <div className="space-y-4">
                {groupKeys.map(dateKey => (
                    <div key={dateKey}>
                        <div className="text-[10px] font-semibold uppercase text-slate-400 mb-2">
                            {formatDateGroup(dateKey + 'T00:00:00Z')}
                        </div>
                        <div className="relative pl-6 space-y-2.5">
                            {/* Vertical timeline line */}
                            <div className="absolute left-1.5 top-1 bottom-1 w-px bg-slate-200" />
                            {groups[dateKey].map((entry) => {
                                const sty = LEVEL_ICON[entry.level] || LEVEL_ICON.INFO;
                                const isExpanded = expandedId === entry.id;
                                return (
                                    <div key={entry.id} className="relative" data-testid={`event-${entry.id}`}>
                                        {/* Timeline dot */}
                                        <div className={`absolute -left-[18px] top-1.5 w-2.5 h-2.5 rounded-full ${sty.dot} ring-2 ring-white`} />
                                        <button
                                            onClick={() => setExpandedId(isExpanded ? null : entry.id)}
                                            className="w-full flex items-start gap-2 text-left hover:bg-slate-50 rounded px-1 py-0.5 -mx-1"
                                        >
                                            <span className="text-[11px] font-mono text-slate-500 w-12 flex-shrink-0">
                                                {formatTimeOnly(entry.created_at)}
                                            </span>
                                            <sty.Icon className={`h-3.5 w-3.5 ${sty.color} flex-shrink-0 mt-0.5`} />
                                            <span className="text-[10px] px-1 py-0 rounded bg-slate-100 text-slate-600 font-medium flex-shrink-0 mt-0.5">
                                                {entry.module}
                                            </span>
                                            <span className="text-xs text-slate-700 flex-1 break-words">
                                                {entry.message}
                                            </span>
                                        </button>
                                        {isExpanded && entry.details && Object.keys(entry.details).length > 0 && (
                                            <pre className="ml-12 mt-1 text-[10px] bg-slate-900 text-slate-100 rounded p-2 overflow-x-auto max-h-48">
{JSON.stringify(entry.details, null, 2)}
                                            </pre>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
        </AdminCollapsibleSection>
    );
}
